import { useState } from 'react';
import { Anchor, Text, Timeline } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { formatDateTime, progressStageLabel } from '../../lib/monitoringLinks';
import { formatReportValue, humanizeKey } from '../../lib/reportFields';
import type { MonitoringProgressEvent } from '../../types/api';
import { DetailSection } from './DetailSection';

/** Steps shown before "Show all"; the rest are one click away. */
const COLLAPSED_COUNT = 8;
/** A phone queues steps while offline, so a later arrival is worth showing. */
const LATE_ARRIVAL_MS = 2 * 60 * 1000;

/** The customer's monitoring check steps (`report.data.monitoringProgress`), newest first. */
export function MonitoringProgressSection({ events }: { events: MonitoringProgressEvent[] }) {
    const { t } = useTranslation();
    const [showAll, setShowAll] = useState(false);

    // Stored in arrival order, so the newest is last.
    const newestFirst = [...events].reverse();
    const visible = showAll ? newestFirst : newestFirst.slice(0, COLLAPSED_COUNT);

    return (
        <DetailSection
            title={t('detail.monitoringProgress')}
            description={t('detail.monitoringProgressHint')}
        >
            <Timeline active={visible.length - 1} bulletSize={14} lineWidth={2}>
                {visible.map((event, index) => {
                    const details = Object.entries(event.details ?? {})
                        .map(([key, value]) => `${humanizeKey(key)}: ${formatReportValue(value)}`)
                        .join(' · ');
                    const occurred = event.occurredAt ? new Date(event.occurredAt).getTime() : NaN;
                    const received = event.receivedAt ? new Date(event.receivedAt).getTime() : NaN;
                    const arrivedLate = received - occurred > LATE_ARRIVAL_MS;

                    return (
                        <Timeline.Item
                            key={event.id ?? `${event.name}-${index}`}
                            title={
                                <Text size="sm" fw={600}>
                                    {progressStageLabel(t, event.name)}
                                </Text>
                            }
                        >
                            {details ? (
                                <Text size="xs" c="dimmed">
                                    {details}
                                </Text>
                            ) : null}
                            <Text size="xs" c="dimmed">
                                {formatDateTime(event.occurredAt) ?? t('common.empty')}
                                {arrivedLate
                                    ? ` · ${t('detail.monitoringProgressReceived', {
                                          when: formatDateTime(event.receivedAt),
                                      })}`
                                    : ''}
                            </Text>
                        </Timeline.Item>
                    );
                })}
            </Timeline>
            {newestFirst.length > COLLAPSED_COUNT ? (
                <Anchor component="button" type="button" size="sm" onClick={() => setShowAll((v) => !v)}>
                    {showAll
                        ? t('detail.monitoringProgressShowLess')
                        : t('detail.monitoringProgressShowAll', { count: newestFirst.length })}
                </Anchor>
            ) : null}
        </DetailSection>
    );
}
